import cn from 'classnames'
import {CSSProperties, FC} from 'react'

import {theme} from '@/theme/theme'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'

export type TextStyleByDevice = {desktop: string; mobile?: string}

export enum TextFontWeight {
  light = 'light',
  regular = 'regular',
  medium = 'medium',
  semibold = 'semibold',
  bold = 'bold',
  extrabold = 'extrabold',
}

export enum TextFontStyle {
  normal = 'normal',
  italic = 'italic',
}

export enum TextVariant {
  h1 = 'h1',
  h2 = 'h2',
  h3 = 'h3',
  h4 = 'h4',
  h5 = 'h5',
  h6 = 'h6',
  text43 = 'text43',
  text24 = 'text24',
  text20 = 'text20',
  text16 = 'text16',
  text14 = 'text14',
  subtitle14 = 'subtitle14',
  subtitle13 = 'subtitle13',
  subtitle12 = 'subtitle12',
  subtitle11 = 'subtitle11',
  caption = 'caption',
}

export type HtmlElementType = keyof JSX.IntrinsicElements

const VariantToHTMLElement: Record<TextVariant, HtmlElementType> = {
  [TextVariant.h1]: 'h1',
  [TextVariant.h2]: 'h2',
  [TextVariant.h3]: 'h3',
  [TextVariant.h4]: 'h4',
  [TextVariant.h5]: 'h5',
  [TextVariant.h6]: 'h6',
  [TextVariant.text43]: 'span',
  [TextVariant.text24]: 'p',
  [TextVariant.text20]: 'p',
  [TextVariant.text16]: 'p',
  [TextVariant.text14]: 'p',
  [TextVariant.subtitle14]: 'span',
  [TextVariant.subtitle13]: 'span',
  [TextVariant.subtitle12]: 'span',
  [TextVariant.subtitle11]: 'span',
  [TextVariant.caption]: 'span',
}

// Headings get their weight from the global h1-h6 styles, not from the variant, so a heading
// variant rendered as a different element needs this weight to keep its look
const getHeadingVariantFontWeight = (variant: TextVariant): TextFontWeight | undefined => {
  if ([TextVariant.h1, TextVariant.h2, TextVariant.h3, TextVariant.h4].includes(variant)) {
    return TextFontWeight.extrabold
  }
  if ([TextVariant.h5, TextVariant.h6].includes(variant)) {
    return TextFontWeight.bold
  }
  return undefined
}

type TextProps = {
  variant?: TextVariant
  mobileVariant?: TextVariant
  element?: HtmlElementType
  color?: string
  align?: 'left' | 'center' | 'right'
  fontWeight?: TextFontWeight
  fontStyle?: TextFontStyle
  className?: string
  dataTest?: string
  numOfTruncatedRows?: number
  alignSelf?: CSSProperties['alignSelf']
}

export const Text: FC<React.PropsWithChildren<TextProps>> = ({
  variant = TextVariant.text16,
  mobileVariant,
  element,
  align,
  color = theme.palette.text.default,
  fontWeight,
  fontStyle = TextFontStyle.normal,
  className,
  dataTest,
  numOfTruncatedRows,
  alignSelf = 'auto',
  children,
}) => {
  const Component = element || VariantToHTMLElement[variant]
  const resolvedFontWeight =
    fontWeight ??
    (Component !== VariantToHTMLElement[variant] ? getHeadingVariantFontWeight(variant) : undefined)
  const typographyStyles = theme.typography[variant] as TextStyleByDevice
  const desktopStyles = typographyStyles.desktop
  const mobileStyles = mobileVariant
    ? (theme.typography[mobileVariant] as TextStyleByDevice).desktop
    : typographyStyles.mobile

  return (
    <>
      <Component
        className={cn('text', align, resolvedFontWeight, className, mobileStyles && 'mobile')}
        data-test={dataTest}
      >
        {children}
      </Component>

      <style jsx>{`
        .text {
          ${desktopStyles};
          color: ${color};
          margin: 0;
          font-style: ${fontStyle};
          align-self: ${alignSelf};
        }
        .left {
          text-align: left;
        }
        .center {
          text-align: center;
        }
        .right {
          text-align: right;
        }
        .light {
          font-weight: ${theme.fontWeight.light};
        }
        .regular {
          font-weight: ${theme.fontWeight.regular};
        }
        .medium {
          font-weight: ${theme.fontWeight.medium};
        }
        .semibold {
          font-weight: ${theme.fontWeight.semibold};
        }
        .bold {
          font-weight: ${theme.fontWeight.bold};
        }
        .extrabold {
          font-weight: ${theme.fontWeight.extrabold};
        }
        .uppercase {
          text-transform: uppercase;
        }
        .lowercase {
          text-transform: lowercase;
        }
        .strikethrough {
          text-decoration: line-through;
        }
        .underline {
          cursor: pointer;
          text-decoration: underline;
        }
        .truncated {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .truncated:not(.inline) {
          width: 100%;
        }
        .truncated-rows {
          text-overflow: ellipsis;
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: ${numOfTruncatedRows};
          -webkit-box-orient: vertical;
        }
        .space-nowrap {
          white-space: nowrap;
        }
        .space-pre-line {
          white-space: pre-line;
        }
        .space-pre-wrap {
          white-space: pre-wrap;
        }
        .wrap-break-word {
          overflow-wrap: break-word;
        }
        .wrap-anywhere {
          overflow-wrap: anywhere;
        }
        .text-block {
          display: block;
        }
        .text-inline-block {
          display: inline-block;
        }
        .text-inline {
          display: inline;
        }
        @media ${MOBILE_SCREENSIZE} {
          .text.mobile {
            ${mobileStyles};
          }
        }
      `}</style>
    </>
  )
}
